import { useEffect, useMemo, useState } from "react";
import {
  Check,
  Copy,
  KeyRound,
  Loader2,
  Plus,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { AdminPage } from "@/components/AdminPage";
import { useToast } from "@/components/Toast";
import { MCP_SCOPES } from "@podecontratar/shared/lib/mcp-scopes";
import { fetchJson, fetchOk } from "@podecontratar/shared/lib/api";

type Scope = string;

interface McpKey {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: Scope[] | null;
  createdBy: string;
  createdByEmail: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

interface ListResponse {
  scopes: Scope[];
  keys: McpKey[];
}

interface CreateResponse extends Omit<McpKey, "createdAt" | "lastUsedAt" | "revokedAt" | "createdBy" | "createdByEmail"> {
  secret: string;
}

export default function AdminMcpPage() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<CreateResponse | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);
  const toast = useToast();

  async function refresh() {
    setLoading(true);
    try {
      setData(await fetchJson<ListResponse>("/api/admin/mcp/keys"));
    } catch {
      toast.push("Falha ao carregar tokens MCP.", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function revoke(id: string) {
    if (!confirm("Revogar este token? Requests subsequentes serão recusadas.")) return;
    try {
      await fetchOk(`/api/admin/mcp/keys/${id}`, { method: "DELETE" });
      toast.push("Token revogado.", "success");
      void refresh();
    } catch {
      toast.push("Não foi possível revogar.", "error");
    }
  }

  return (
    <AdminPage
      eyebrow="Integrações"
      title="Tokens MCP"
      description="Bearer tokens consumidos pelo worker mcp.podecontratar.com.br. O valor bruto só é exibido uma vez no momento da criação."
      action={
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3.5 py-2 text-sm font-medium text-text-inverse transition-[background-color,transform] duration-150 hover:bg-accent-hover active:translate-y-px"
        >
          <Plus size={14} strokeWidth={2.25} aria-hidden />
          Novo token
        </button>
      }
    >
      {loading && !data ? (
        <div className="flex items-center justify-center rounded-3xl border border-border bg-surface p-12">
          <Loader2 size={20} className="animate-spin text-text-tertiary" />
        </div>
      ) : data && data.keys.length === 0 ? (
        <div className="rounded-3xl border border-border bg-surface p-10 text-center">
          <KeyRound size={28} className="mx-auto text-text-tertiary" />
          <p className="mt-3 text-sm text-text-secondary">
            Nenhum token MCP. Clique em <strong className="text-text">Novo token</strong>{" "}
            para criar o primeiro.
          </p>
        </div>
      ) : data ? (
        <section className="rounded-3xl border border-border bg-surface">
          <ul className="divide-y divide-border">
            {data.keys.map((k) => (
              <KeyRow key={k.id} k={k} onRevoke={() => revoke(k.id)} />
            ))}
          </ul>
        </section>
      ) : null}

      {creating && (
        <CreateDialog
          // Fallback to the local constant if the list endpoint hasn't
          // returned yet (or 500'd) — the scope vocabulary is stable enough
          // to live on the frontend, and locking the dialog behind a
          // network round-trip was the bug a previous version of this page
          // shipped with.
          allScopes={data?.scopes ?? [...MCP_SCOPES]}
          onCancel={() => setCreating(false)}
          onCreated={(res) => {
            setCreating(false);
            setRevealed(res);
            setSecretCopied(false);
            void refresh();
          }}
        />
      )}

      {revealed && (
        <SecretRevealDialog
          k={revealed}
          copied={secretCopied}
          onCopy={async () => {
            try {
              await navigator.clipboard.writeText(revealed.secret);
              setSecretCopied(true);
            } catch {
              toast.push("Não consegui copiar — copie manualmente.", "error");
            }
          }}
          onClose={() => setRevealed(null)}
        />
      )}
    </AdminPage>
  );
}

// ---------------------------------------------------------------------------

function KeyRow({ k, onRevoke }: { k: McpKey; onRevoke: () => void }) {
  const isRevoked = k.revokedAt !== null;
  const access = k.scopes === null ? "Acesso total" : `${k.scopes.length} permissões`;
  return (
    <li className="flex flex-wrap items-center justify-between gap-4 p-5">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className={`truncate text-sm font-semibold tracking-tight ${isRevoked ? "text-text-tertiary line-through" : "text-text"}`}>
            {k.name}
          </p>
          {k.scopes === null && !isRevoked && (
            <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-warning">
              <ShieldCheck size={10} strokeWidth={2.25} aria-hidden />
              full
            </span>
          )}
          {isRevoked && (
            <span className="rounded-full bg-danger/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-danger">
              revogado
            </span>
          )}
        </div>
        <p className="mt-1 font-mono text-xs text-text-tertiary">
          {k.keyPrefix}… · {access}
        </p>
        <p className="mt-0.5 text-[11px] text-text-tertiary">
          Criado por {k.createdByEmail ?? k.createdBy} em{" "}
          {new Date(k.createdAt).toLocaleDateString("pt-BR")} ·{" "}
          {k.lastUsedAt
            ? `último uso em ${new Date(k.lastUsedAt).toLocaleString("pt-BR")}`
            : "nunca usado"}
        </p>
      </div>
      {!isRevoked && (
        <button
          type="button"
          onClick={onRevoke}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:border-danger/40 hover:text-danger"
        >
          <Trash2 size={12} strokeWidth={2} aria-hidden />
          Revogar
        </button>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------

function CreateDialog({
  allScopes,
  onCancel,
  onCreated,
}: {
  allScopes: Scope[];
  onCancel: () => void;
  onCreated: (res: CreateResponse) => void;
}) {
  const [name, setName] = useState("");
  const [fullAccess, setFullAccess] = useState(false);
  const [selected, setSelected] = useState<Set<Scope>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const groups = useMemo(() => groupScopes(allScopes), [allScopes]);

  async function submit() {
    if (name.trim().length < 2) {
      toast.push("Dê um nome ao token.", "error");
      return;
    }
    if (!fullAccess && selected.size === 0) {
      toast.push("Selecione ao menos uma permissão.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const out = await fetchJson<CreateResponse>("/api/admin/mcp/keys", {
        method: "POST",
        json: {
          name: name.trim(),
          scopes: fullAccess ? [] : Array.from(selected),
          full_access: fullAccess,
        },
      });
      onCreated(out);
    } catch {
      toast.push("Falha ao criar token.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DialogShell title="Novo token MCP" onClose={onCancel}>
      <div className="space-y-6">
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-[0.22em] text-text-tertiary">
            Nome
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Claude Desktop · Costa"
            className="mt-2 w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm text-text focus:border-accent focus:outline-none"
          />
        </label>

        <div>
          <label className="flex items-center gap-2.5">
            <input
              type="checkbox"
              checked={fullAccess}
              onChange={(e) => setFullAccess(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-accent"
            />
            <span className="text-sm font-medium text-text">Acesso total (admin)</span>
            <span className="text-xs text-text-tertiary">— pula a checagem por escopo</span>
          </label>
        </div>

        {!fullAccess && (
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-text-tertiary">
              Permissões
            </p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              {Object.entries(groups).map(([resource, ops]) => (
                <fieldset
                  key={resource}
                  className="rounded-xl border border-border bg-surface-alt p-3"
                >
                  <legend className="px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-text-secondary">
                    {resource}
                  </legend>
                  <div className="mt-1 space-y-1.5">
                    {ops.map((scope) => (
                      <label key={scope} className="flex items-center gap-2 text-sm text-text">
                        <input
                          type="checkbox"
                          checked={selected.has(scope)}
                          onChange={(e) => {
                            setSelected((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(scope);
                              else next.delete(scope);
                              return next;
                            });
                          }}
                          className="h-3.5 w-3.5 rounded border-border accent-accent"
                        />
                        <span className="font-mono text-xs">{scope.split(":")[1]}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-7 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-alt"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-text-inverse transition-[background-color,transform] duration-150 hover:bg-accent-hover active:translate-y-px disabled:opacity-50"
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          Criar token
        </button>
      </div>
    </DialogShell>
  );
}

// ---------------------------------------------------------------------------

function SecretRevealDialog({
  k,
  copied,
  onCopy,
  onClose,
}: {
  k: CreateResponse;
  copied: boolean;
  onCopy: () => void;
  onClose: () => void;
}) {
  return (
    <DialogShell title="Token criado" onClose={onClose}>
      <p className="text-sm text-text-secondary">
        Copie e guarde o valor agora. Por segurança, ele não é mostrado novamente — só fica
        o hash no banco.
      </p>
      <div className="mt-5 rounded-xl border border-warning/40 bg-warning/5 p-4">
        <p className="font-mono text-xs leading-relaxed break-all text-text">{k.secret}</p>
      </div>
      <div className="mt-6 flex items-center justify-between">
        <p className="text-xs text-text-tertiary">
          {k.name} · {k.scopes === null ? "acesso total" : `${k.scopes.length} permissões`}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3.5 py-2 text-xs font-medium text-text transition-colors hover:bg-surface-alt"
          >
            {copied ? (
              <>
                <Check size={13} strokeWidth={2.25} aria-hidden /> Copiado
              </>
            ) : (
              <>
                <Copy size={13} strokeWidth={2} aria-hidden /> Copiar
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-accent px-3.5 py-2 text-xs font-medium text-text-inverse transition-[background-color,transform] duration-150 hover:bg-accent-hover active:translate-y-px"
          >
            Fechar
          </button>
        </div>
      </div>
    </DialogShell>
  );
}

// ---------------------------------------------------------------------------

function DialogShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-xl rounded-3xl border border-border bg-surface p-7 shadow-[var(--shadow-card-lift)]">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-base font-semibold tracking-tight text-text">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-lg p-1.5 text-text-tertiary transition-colors hover:bg-surface-alt hover:text-text"
          >
            <X size={16} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

function groupScopes(scopes: Scope[]): Record<string, Scope[]> {
  const groups: Record<string, Scope[]> = {};
  for (const s of scopes) {
    const [resource] = s.split(":");
    if (!groups[resource]) groups[resource] = [];
    groups[resource].push(s);
  }
  return groups;
}
